import React, { useEffect, useRef, useState } from "react";

import Button from "../main/button";
import Input from "../main/input";

import fetch from "../../utils/fetch";

import { useModalDataStore } from "../../store/modalData";

interface LocationResult {
    name: string;
    address_line_1: string;
    address_line_2: string;
    address_line_3: string;
    city: string;
    state: string;
    postcode: string;
    country: string;
    distance_text: string;
    duration_text: string;
    distance_value: number;
    duration_value: number;
}

interface Props {}

const ClosestLocation: React.FC<Props> = () => {
    const modalData = useModalDataStore<any>((s) => s.modalData);
    const setModalData = useModalDataStore<any>((s) => s.setModalData);

    const [callerAddress, setCallerAddress] = useState<string>("");
    const [loading, setLoading] = useState<boolean>(false);
    const [results, setResults] = useState<LocationResult[]>([]);
    const [searchedAddress, setSearchedAddress] = useState<string>("");
    const [errorMsg, setErrorMsg] = useState<string>("");

    const isMounted = useRef<boolean>(true);

    useEffect(() => {
        isMounted.current = true;
        return () => {
            isMounted.current = false;
        };
    }, []);

    const formatAddress = (r: LocationResult) => [r.address_line_1, r.address_line_2, r.address_line_3, r.city, r.state, r.postcode].filter(Boolean).join(", ");

    const find_closest_location = async () => {
        if (!callerAddress.trim()) return;

        setErrorMsg("");
        setResults([]);
        setLoading(true);

        try {
            const post = new fetch(
                "/user/vca/closest-location",
                {
                    caller_address: callerAddress.trim(),
                    account_id: modalData?.data?.id,
                },
                { success: false, error: true },
            ).post();

            post.then((res: any) => {
                if (!isMounted.current) return;

                if (res.status === "success") {
                    setResults(res.data || []);
                    setSearchedAddress(callerAddress.trim());
                } else {
                    alert("Could not find results. Please try a more specific address.");
                }

                setLoading(false);
            }).catch(() => {
                if (!isMounted.current) return;
                alert("Something went wrong. Please try again.");
                setLoading(false);
            });
        } catch (e) {
            if (!isMounted.current) return;
            alert("Something went wrong. Please try again.");
            setLoading(false);
        }
    };

    const closest = results[0] || null;

    return (
        <div className="container-fluid">
            {/* Search */}
            <div className="row mb-3">
                <div className="col-12">
                    <div className="fw-600 mb-2">Find Closest Location</div>
                    <Input
                        container={false}
                        className="input-small"
                        name="calleraddress"
                        placeholder="Type caller's suburb, postcode or address"
                        type="text"
                        defaultValue={callerAddress}
                        changeDefault={new Date().getTime()}
                        updateChange={(v: string) => setCallerAddress(v)}
                    />
                    <Button
                        className="w-100 mt-1"
                        color="blue"
                        hideContainer
                        small
                        active={loading}
                        disabled={!callerAddress.trim() || loading}
                        value={loading ? "Searching..." : "Find Closest Location"}
                        onClick={() => find_closest_location()}
                    />
                </div>
            </div>

            {/* Error */}
            {errorMsg && (
                <div className="col-12 mb-3">
                    <div className="small red">{errorMsg}</div>
                </div>
            )}

            {/* Results */}
            {results.length > 0 && (
                <>
                    <div className="row mb-2">
                        <div className="col-12">
                            <div className="small grey">
                                Results for: <span className="fw-600">{searchedAddress}</span>
                            </div>
                        </div>
                    </div>

                    {/* Closest highlight */}
                    {closest && (
                        <div
                            className="col-12 mb-3 p-3"
                            style={{
                                backgroundColor: "#e8f5e9",
                                border: "2px solid #4caf50",
                                borderRadius: "6px",
                            }}
                        >
                            <div className="small green fw-600 mb-1">★ CLOSEST LOCATION</div>
                            <div className="fw-600">{closest.name || "Location"}</div>
                            <div className="small mb-2">{formatAddress(closest)}</div>
                            <div className="row">
                                <div className="col-6">
                                    <div className="small grey">Distance</div>
                                    <div className="fw-600 small">{closest.distance_text}</div>
                                </div>
                                <div className="col-6">
                                    <div className="small grey">Drive time</div>
                                    <div className="fw-600 small">{closest.duration_text}</div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* All locations ranked */}
                    {results.length > 1 && (
                        <>
                            <div className="col-12 mb-2">
                                <div className="small grey fw-600">All Locations (by distance)</div>
                            </div>
                            {results.map((r, i) => (
                                <div
                                    key={i}
                                    className="col-12 mb-2 p-2"
                                    style={{
                                        border: "1px solid #ddd",
                                        borderRadius: "4px",
                                        backgroundColor: i === 0 ? "#f9fbe7" : "#fff",
                                    }}
                                >
                                    <div className="row" style={{ alignItems: "center" }}>
                                        <div className="col-1 text-center">
                                            <span className="fw-600 small" style={{ color: i === 0 ? "#4caf50" : "#999" }}>
                                                #{i + 1}
                                            </span>
                                        </div>
                                        <div className="col-7">
                                            <div className="fw-600 small">{r.name || `Location ${i + 1}`}</div>
                                            <div className="small grey">{formatAddress(r)}</div>
                                        </div>
                                        <div className="col-4 text-right">
                                            <div className="small fw-600">{r.distance_text}</div>
                                            <div className="small grey">{r.duration_text}</div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </>
                    )}
                </>
            )}
        </div>
    );
};

export default ClosestLocation;
